/**
 * 出力の比べ方。
 * 既定 (exact) は、改行を \n に揃え、行ごとに右の空白を落とし、末尾の空行を落としてから完全一致。
 * 左の空白と行の中の空白は見る (本番の試験の多くも同じ)。
 * float は、行と、行の中の語の数が同じで、数の語は絶対誤差か相対誤差が tolerance 以下、数でない語は完全一致なら通す。
 * ただし整数どうし (小数点も指数も無い語) は完全一致だけで、誤差は当てない。
 */
import type { CompareMode } from "./types";

export const FLOAT_TOLERANCE = 1e-6;

export function normalizeOutput(text: string): string {
  const lines = text.replace(/\r\n?/g, "\n").split("\n").map((line) => line.replace(/[ \t\f\v]+$/, ""));
  while (lines.length > 0 && lines[lines.length - 1] === "") lines.pop();
  return lines.join("\n");
}

const NUMBER = /^[+-]?(?:\d+\.?\d*|\.\d+)(?:[eE][+-]?\d+)?$/;

function sameNumber(a: number, b: number, tolerance: number): boolean {
  if (a === b) return true;
  const diff = Math.abs(a - b);
  return diff <= tolerance || diff <= tolerance * Math.max(Math.abs(a), Math.abs(b));
}

/** 小数点も指数も無い整数の語 */
const INTEGER = /^[+-]?\d+$/;

function sameWord(a: string, b: string, tolerance: number): boolean {
  if (a === b) return true;
  /* 整数どうしは完全一致だけ。相対誤差を当てると、123456789 と 123456900 のような整数の誤りが通る */
  if (INTEGER.test(a) && INTEGER.test(b)) return false;
  if (!NUMBER.test(a) || !NUMBER.test(b)) return false;
  return sameNumber(Number(a), Number(b), tolerance);
}

export function compareOutput(actual: string, expected: string, mode: CompareMode, tolerance = FLOAT_TOLERANCE): boolean {
  const a = normalizeOutput(actual);
  const e = normalizeOutput(expected);
  if (mode === "exact") return a === e;
  const aLines = a === "" ? [] : a.split("\n");
  const eLines = e === "" ? [] : e.split("\n");
  if (aLines.length !== eLines.length) return false;
  return eLines.every((line, i) => {
    const aw = aLines[i].split(/\s+/).filter(Boolean);
    const ew = line.split(/\s+/).filter(Boolean);
    return aw.length === ew.length && ew.every((word, j) => sameWord(aw[j], word, tolerance));
  });
}
